/**
 * storage.ts — Browser localStorage persistence for user profile + preferences.
 *
 * HC-NO-PERSONAL-DATA: data never leaves the browser.
 * Keys:
 *   '312-finance-profile'     — UserProfile fields
 *   '312-finance-remember-me' — boolean preference
 */

import type { UserProfile } from '../engine/types'

const PROFILE_KEY    = '312-finance-profile'
const REMEMBER_KEY   = '312-finance-remember-me'

// ─── Profile ─────────────────────────────────────────────────────────────────

export function loadSavedProfile(): UserProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as UserProfile
  } catch {
    return null
  }
}

export function saveProfile(profile: UserProfile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile))
  } catch {
    // storage quota exceeded or private mode — silently skip
  }
}

export function clearSavedProfile(): void {
  try {
    localStorage.removeItem(PROFILE_KEY)
  } catch { /* no-op */ }
}

// ─── Remember-me preference ──────────────────────────────────────────────────

export function loadRememberMe(): boolean {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY)
    if (raw === null) return true   // default opt-in
    return raw === 'true'
  } catch {
    return true
  }
}

export function saveRememberMe(value: boolean): void {
  try {
    localStorage.setItem(REMEMBER_KEY, String(value))
  } catch { /* no-op */ }
}
